import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { FormGroup, Label } from 'reactstrap';

import { Button, Card, Flex, Form, Select } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { downloadCSV } from '../../../helpers/common';
import { formatAnimalList, formatAnimalsPayload } from '../../../helpers/livestock';
import { getTableColumns, planHasFeature } from '../../../helpers/subscription';
import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT, SEX } from '../../../constants/common';
import { FEATURES } from '../../../constants/features';
import { ANIMAL_ERRORS } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { FEATURE_LIST } from '../../../constants/subscription';
import { TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';
import { LIVESTOCK_CSV_TEMPLATE } from '../../../constants/templates';
import { LIVESTOCK_CSV_VALIDATION_SCHEMA } from '../../../constants/validation-schemas';

import { useLivestockColumns } from '../../../hooks/useLivestockColumns';
import { useModal } from '../../../hooks/useModal';

import { deleteManyAnimals, fetchAnimals, fetchStockTypes } from '../../../redux/actions/animal';
import { fetchBreeds } from '../../../redux/actions/breed';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchGeofences } from '../../../redux/actions/geofence';
import { fetchLabels } from '../../../redux/actions/label';
import { fetchTags } from '../../../redux/actions/tag';
import { setSortedData } from '../../../redux/reducers/animal';
import { setSelectedFilters } from '../../../redux/reducers/filters';

import AppTable from '../../../components/AppTable';
import { errorToastHandler, infoToastHandler, successToastHandler } from '../../../components/action_notifier';
import Livestock from '../../../components/animals/Livestock';

import { AnimalService } from '../../../services';
import { useFilterOptions } from './useFilterOptions';

const AnimalPopup = lazy(() => import('../../../components/animal_popup'));
const AppModal = lazy(() => import('../../../components/AppModal'));
const UploadModal = lazy(() => import('../../../components/Modals/UploadModal'));

function Livestocks() {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);

  const { count, data: animals, loading, sortedData } = useSelector(state => state.animal);
  const { isAdmin, permissions } = useSelector(state => state.user);
  const { list: farms, options: farmOptions, loading: farmsLoading } = useSelector(state => state.farm);
  const { myPlan } = useSelector(state => state.subscription);
  const { options: labelOptions } = useSelector(state => state.label);
  const { selectedFilters } = useSelector(state => state.filters);
  const { tags } = useSelector(state => state.tag);

  const [submittable, setSubmittable] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [state, setState] = useState({
    csv_farm: undefined,
    delete_animals: [],
    editableIds: [],
    isGeofenceChanged: false,
    label_modal: false,
    link_modal: false,
    selected_animals: [],
    selected_animals_linked: [],
    selected_animals_unlinked: [],
    selected_tags: [],
    showAnimalPopup: false,
    showPopup: false,
    tableData: [],
    tempModalValue: null,
    uploadSuccessMessage: ''
  });

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [confirmModalVisible, openConfirmModal, closeConfirmModal] = useModal();
  const [uploadModalVisible, openUploadModal, closeUploadModal] = useModal();
  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (farmOptions?.length) {
      setState(prevState => ({ ...prevState, csv_farm: farmOptions?.at(0) }));
    }
  }, [farmOptions]);

  const animalsList = useMemo(() => formatAnimalList(animals), [animals]);
  const maleLivestocks = useMemo(() => animalsList?.filter(s => s.sex === SEX.MALE), [animalsList]);
  const femaleLivestocks = useMemo(() => animalsList?.filter(s => s.sex === SEX.FEMALE), [animalsList]);

  useEffect(() => {
    if (animals?.length) {
      setState(prevState => ({
        ...prevState,
        animals: [...animalsList],
        tableData: [...animalsList],
        maleLivestocks,
        femaleLivestocks
      }));
      dispatch(setSortedData(animals));
    }
  }, [animals, animalsList, femaleLivestocks, maleLivestocks]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && animals?.length) {
      dispatch(setSortedData(animals));
    }
  }, [filteredInfo]);

  const clearFilters = useCallback(() => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  }, [setFilteredInfo, dispatch]);

  const selectRecords = useCallback(
    selected => {
      setState(prevState => ({
        ...prevState,
        selected_animals: selected
      }));
    },
    [state.selected_animals]
  );

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilteredInfo(filters);
      dispatch(setSelectedFilters(filters));
      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, dispatch]
  );

  async function fetchData() {
    try {
      await Promise.all([
        getTags(),
        getLivestocks(),
        getFarms(),
        getLabels(),
        dispatch(fetchGeofences()),
        dispatch(fetchBreeds()),
        getStockTypes()
      ]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function getTags() {
    if (!planHasFeature(myPlan, FEATURES.TAGS) || !hasPermission(permissions, PERMISSIONS.TAGS.VIEW_OWN)) {
      return;
    }

    await dispatch(fetchTags({ only_linked: false })).unwrap();
  }

  async function getLabels() {
    await dispatch(fetchLabels({ type: 'custom' })).unwrap();
  }

  async function getLivestocks(query = '') {
    const params = {
      query,
      farm_ids: state.filters?.farms?.map(x => x.value),
      geofence_ids: state.filters?.geofences?.map(x => x.value),
      label_ids: state.filters?.labels?.map(x => x.value)
    };

    try {
      await dispatch(fetchAnimals(params)).unwrap();
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getFarms() {
    await dispatch(fetchFarms()).unwrap();
  }

  async function getStockTypes() {
    await dispatch(fetchStockTypes()).unwrap();
  }

  const downloadCsv = useCallback(async () => {
    const url = state.selected_animals?.length
      ? `/animals/export?livestock_ids=${state.selected_animals}`
      : '/animals/template/export';

    try {
      const response = await axios.get(url, { responseType: 'blob' });

      downloadCSV(response?.data, `Livestock_${dayjs().format(DATE_FORMAT.DATE)}.xlsx`);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_DOWNLOAD_CSV);
    }
  }, [state.selected_animals]);

  const setDeleteTags = useCallback(async () => {
    setState(prevState => ({ ...prevState, delete_animals: state.selected_animals }));
    openArchiveModal();
  }, [state.selected_animals]);

  const actions = useMemo(() => {
    const availableActions = [];
    const isAnyAnimalSelected = state.selected_animals?.length;

    if (hasPermission([PERMISSIONS.ANIMALS.CREATE], permissions)) {
      availableActions.push(
        {
          label: (
            <Link className="app-link" to="/animals/new">
              Add livestock
            </Link>
          )
        },
        {
          label: (
            <Button color="default" size="small" variant="link" onClick={openUploadModal}>
              Upload livestock
            </Button>
          )
        }
      );
    }

    if (hasPermission([PERMISSIONS.ANIMALS.VIEW_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={downloadCsv}>
            Download livestock
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.ANIMALS.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" disabled={!isAnyAnimalSelected} onClick={setDeleteTags}>
            Archive livestock
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [state.selected_animals?.length, permissions, selectedFilters, clearFilters, downloadCsv, setDeleteTags]);

  async function handleTableValueChange(key, value, record, selectedRecord) {
    const data = [...state.tableData];
    const rowIndex = data.findIndex(item => item.id === record?.id);

    if (rowIndex !== -1) {
      data[rowIndex] = {
        ...data[rowIndex],
        [key]: value,
        [key === 'sireId' ? 'sire_identifier' : 'dam_identifier']: selectedRecord?.identifier || null
      };
      await onClickSaveEdit([data[rowIndex]]);

      setState(prevState => ({ ...prevState, tableData: data }));
    }
  }

  async function uploadCSV(file) {
    try {
      const formData = new FormData();

      formData.append('file', file);
      formData.append('farm_id', state.csv_farm?.id);

      const config = { headers: { 'content-type': 'multipart/form-data' } };

      const response = await axios.post('animals/csv', formData, config);

      if (response.status === 200) {
        await getLivestocks();

        setState(prevState => ({ ...prevState, uploadSuccessMessage: response.data.message }));

        closeUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function linkTag() {
    const animalId = state.selected_animals?.at(0);
    const animal = animals?.find(a => a.id === animalId);
    let response;

    if (state.re_linking) {
      response = await axios.put('links', {
        tag_id: state.selected_tags?.at(0),
        animal_id: animal.id
      });
    } else {
      response = await axios.post('links', {
        tag_id: state.selected_tags?.at(0),
        animal_id: animal.id
      });
    }

    if (response.status === 200) {
      await getTags();
      toggleModal('link_modal');
      setState(prevState => ({ ...prevState, re_linking: false, linking: false }));

      window.location.reload();
    }
  }

  function handleCSVFarmChange(newValue) {
    const selectedFarm = farmOptions.find(item => item.id === newValue);
    setState(prevState => ({ ...prevState, csv_farm: selectedFarm }));
  }

  function toggleModal(modal) {
    setState(prevState => ({ ...prevState, [modal]: !state[modal] }));
  }

  async function deleteTag() {
    let isAnimalWithTag = false;
    let animalMissingLossOrSoldDate = false;

    for (let index = 0; index < state.selected_animals.length; index++) {
      const animalId = state?.selected_animals?.[index];
      const animal = animals.find(a => a.id === animalId);
      if (animal?.tag?.id) {
        isAnimalWithTag = true;
      }

      if (!animal.loss_date && !animal.date_sold) {
        animalMissingLossOrSoldDate = true;
      }
    }

    if (isAnimalWithTag) {
      infoToastHandler(messages.ANIMAL_UNLINK_TAG_REQUIRED);
    } else if (animalMissingLossOrSoldDate) {
      infoToastHandler(messages.LOSS_OR_SOLD_DATE_REQUIRED_BEFORE_DELETE);
    } else {
      try {
        await dispatch(deleteManyAnimals({ ids: state.selected_animals })).unwrap();
        await getTags();
        await getLivestocks();

        closeArchiveModal();
        successToastHandler(messages.ARCHIVED);
      } catch (error) {
        errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
      }
    }
  }

  function getLabel(id) {
    const filteredTags = tags?.filter(t => t.id === id);

    return filteredTags?.length > 0 ? filteredTags.at(0).diagri_id : '';
  }

  function handleLabelAddChange(selectedOption) {
    setState(prevState => ({ ...prevState, selected_labels_add: selectedOption }));
  }

  async function linkLabels() {
    const response = await axios.post('/labels/bulk/animals', {
      animal_ids: state.selected_animals_unlinked.concat(state.selected_animals_linked),
      label_ids: state.selected_labels_add.map(x => x.value)
    });

    if (response.status === 200) toggleModal('label_modal');
  }

  async function onClickSaveEdit(data) {
    const updatedData = !data
      ? state.tableData.filter(item => state.editableIds?.includes(item.id))
      : Array.isArray(data)
        ? data
        : [data];

    if (!updatedData) return;

    const additionalData = { farms };
    const payload = { animals: formatAnimalsPayload(updatedData, additionalData) };

    try {
      await AnimalService.updateAnimals(payload);

      setState(prevState => ({
        ...prevState,
        editableIds: [],
        selected_animals: [],
        selected_animals_linked: [],
        selected_animals_unlinked: []
      }));

      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  function showAnimalPopup(key, liveStock) {
    setState(prevState => ({
      ...prevState,
      showPopup: true,
      animal_popup_field: key || null,
      filtered_popup_animals: liveStock || []
    }));
  }

  function onAnimalSelect(field, value, selectedRecord) {
    if (field === 'sire_id') field = 'sireId';
    if (field === 'dam_id') field = 'damId';

    handleTableValueChange(field, value, state.tempModalValue, selectedRecord);
    closeAnimalPopup();
  }

  function closeAnimalPopup() {
    setState(prevState => ({
      ...prevState,
      showPopup: false,
      animal_popup_field: null,
      filtered_popup_animals: [],
      tempModalValue: null
    }));
  }

  const handleSelect = (male, record) => {
    setState(prevState => ({ ...prevState, tempModalValue: record }));

    const siresWithoutCurrentAnimal = state.maleLivestocks?.filter(m => m.id !== record.id);
    const damsWithoutCurrentAnimal = state.femaleLivestocks?.filter(m => m.id !== record.id);

    showAnimalPopup(male ? 'sire_id' : 'dam_id', male ? siresWithoutCurrentAnimal : damsWithoutCurrentAnimal);
  };

  const handleAssignedGeofenceChange = ({ recordId, tableData, isGeofenceChanged }) => {
    setState(prev => ({ ...prev, editableIds: [recordId], tableData, isGeofenceChanged }));
    openConfirmModal();
  };

  const columnFilterOptions = useFilterOptions({ animals: sortedData });
  const columns = useLivestockColumns({
    columnFilterOptions,
    filteredInfo,
    tableData: state.tableData,
    handleAssignedGeofenceChange,
    handleSelect,
    handleTableValueChange
  });

  function filterObjectKeys(data, keysToKeep) {
    return data?.map(obj => {
      return Object.fromEntries(Object.entries(obj).filter(([key]) => keysToKeep.includes(key)));
    });
  }

  const tableColumns = isAdmin ? columns : getTableColumns(columns, myPlan?.type, FEATURE_LIST.LIVESTOCK_LIST);
  const template = filterObjectKeys(
    LIVESTOCK_CSV_TEMPLATE,
    tableColumns.map(c => {
      if (c.dataIndex === 'breedName') {
        return 'breed';
      } else if (c.dataIndex === 'stock_type') {
        return 'livestock_type';
      } else if (c.dataIndex === 'sire_identifier') {
        return 'sire_id';
      } else if (c.dataIndex === 'dam_identifier') {
        return 'dam_id';
      }

      return c.dataIndex;
    })
  );

  const assignLivestock = useCallback(async () => {
    await onClickSaveEdit();
    closeConfirmModal();
  }, [state.editableIds, state.tableData, state.isGeofenceChanged]);

  return (
    <>
      <Card className="mb-0">
        <AppTable
          description="List of Livestock"
          headerClass="py-2"
          searchPlaceholder="Search Livestock"
          title="Livestock"
          actions={actions}
          baseColumns={tableColumns}
          dataSource={state.tableData}
          loading={loading}
          tableConfigKey={TABLE_CONFIG_KEYS.LIVESTOCK}
          totalCount={count}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getLivestocks}
          updateTableData={onClickSaveEdit}
        />
      </Card>

      <Suspense fallback={null}>
        <AppModal
          isOpen={archiveModalVisible}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive livestocks"
          handleCancel={closeArchiveModal}
          handleConfirm={deleteTag}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('livestock(s)')}</div>
        </AppModal>

        <AppModal
          isOpen={state.link_modal}
          confirmButtonText="Link"
          title="Linking confirmation"
          handleCancel={() => toggleModal('link_modal')}
          handleConfirm={linkTag}>
          <p className="text-center">
            Tag <b>{getLabel(state.selected_tags?.at(0))}</b> will be linked to
          </p>
          <Livestock animals={animals} animal={state.selected_animals_unlinked} />
          <Flex gap="small" justify="end">
            <Button color="secondary" onClick={() => history.push('/animals/' + state.selected_animals_unlinked)}>
              View Livestock
            </Button>
            <Button className="float-right" color="primary" onClick={() => history.push('/tracking')}>
              Go to Tracking
            </Button>
          </Flex>
        </AppModal>

        <UploadModal
          fileName="livestock-template.csv"
          formInitValues={{ farm: state.csv_farm }}
          formRef={formRef}
          isOpen={uploadModalVisible}
          isReturnCsv={false}
          schema={LIVESTOCK_CSV_VALIDATION_SCHEMA}
          submittable={submittable}
          template={template}
          description={
            <>
              <p className="mb-0">You can download the livestock list to input a new livestock.</p>
              <p className="mb-0">Download and save the file and then choose file to upload new animals.</p>
            </>
          }
          onCancel={closeUploadModal}
          onUpload={uploadCSV}
          setSubmittable={setSubmittable}>
          <Form.Item
            label="Select a farm"
            name="farm"
            colon={false}
            labelAlign="left"
            rules={[{ required: true, message: messages.FIELD_REQUIRED('Farm') }]}>
            <Select className="w-full" loading={farmsLoading} options={farmOptions} onChange={handleCSVFarmChange} />
          </Form.Item>
        </UploadModal>

        <AppModal
          confirmButtonText="Ok"
          cancelButtonHidden={true}
          isOpen={uploadSuccessModalVisible}
          handleConfirm={closeUploadSuccessModal}>
          <Flex align="center" justify="center" className="pb-2 pt-5">
            {state.uploadSuccessMessage}
          </Flex>
        </AppModal>

        <AppModal
          isOpen={state.label_modal}
          confirmButtonText="Link Groups"
          title="Link Groups"
          handleCancel={() => toggleModal('label_modal')}
          handleConfirm={linkLabels}>
          <FormGroup>
            <Label className="mr-2">Livestock Group</Label>
            <Select
              isMulti={true}
              isSearchable={true}
              options={labelOptions}
              style={{ minWidth: '300px' }}
              value={state.selected_labels_add}
              onChange={handleLabelAddChange}
            />
          </FormGroup>
        </AppModal>

        {state.showPopup && (
          <AnimalPopup
            key="animal-popup"
            animals={state.filtered_popup_animals}
            fieldKey={state.animal_popup_field}
            onClose={closeAnimalPopup}
            onSelect={onAnimalSelect}
          />
        )}

        <AppModal
          isOpen={confirmModalVisible}
          confirmButtonText="Yes"
          handleCancel={closeConfirmModal}
          handleConfirm={assignLivestock}>
          <div className="py-4">{ANIMAL_ERRORS.ASSIGNED_TO_DIFFERENT_GEOFENCE}</div>
        </AppModal>
      </Suspense>
    </>
  );
}

export default memo(Livestocks);
